import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import HeroBanner from '../../components/SkillMarketplace/HeroBanner';
import SkillCategorySection, {
  SkillCategorySectionProps,
} from '../../components/SkillMarketplace/SkillCategorySection';
import MySkillsGrid, {
  MySkillsCategory,
} from '../../components/SkillMarketplace/MySkillsGrid';
import {
  getSkillClient,
  ListCatalogCommand,
  ListEquipmentCommand,
  EquipSkillCommand,
  UnequipSkillCommand,
  ListTagsCommand,
  type CatalogItem,
  type TagItem,
} from '../../client/SkillClient';
import { useAuth } from '../../context/AuthContext';
import { useImpersonation } from '../../context/ImpersonationContext';
import { useAgent } from '../../context/AgentContext';
import { useLocale } from '../../i18n/useLocale';
import SkillDetailModal from '../../components/SkillMarketplace/SkillDetailModal';
import SkillCard from '../../components/SkillMarketplace/SkillCard';
import type { SkillCardProps } from '../../components/SkillMarketplace/SkillCard';
import { categoriesFor } from '../../utils/skillCategories';
import { skillDescription, skillSearchText } from './skillDescription';
import './SkillMarketplace.css';

// ── Category config ─────────────────────────────────────────────────────────

// Tab ids drive the page; only Recommend and My Skills have translated labels (categories are brands).
const TAB_LABEL_KEYS: Record<string, 'tabs.recommend' | 'tabs.mySkills'> = { Recommend: 'tabs.recommend', 'My Skills': 'tabs.mySkills' };

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Group for skills without tags. A data key; shown as skills:generalTag. */
const GENERAL_TAG = 'General';

/** Derive thumbnail S3 key from s3_location (SKILL.md path) */
function deriveThumbnailUrl(s3Location?: string): string | undefined {
  return s3Location || undefined;
}

function matchesQuery(text: string, query: string): boolean {
  return text.toLowerCase().includes(query.toLowerCase());
}

function filterCategories(
  categories: SkillCategorySectionProps[],
  query: string,
): SkillCategorySectionProps[] {
  if (!query) return categories;
  return categories
    .map((cat) => ({
      ...cat,
      skills: cat.skills.filter(
        (s) =>
          matchesQuery(s.title, query) ||
          matchesQuery(s.description, query) ||
          matchesQuery(s.searchText ?? '', query) ||
          s.tags.some((t) => matchesQuery(t, query)),
      ),
    }))
    .filter((cat) => cat.skills.length > 0);
}

// ── Hook: catalog + equipment ───────────────────────────────────────────────

function useSkillMarketplace() {
  const { uid } = useAuth();
  const { effectiveUid } = useImpersonation();
  const { currentAgent } = useAgent();
  const activeUid = effectiveUid || uid;
  const [itemsByCategory, setItemsByCategory] = useState<Record<string, CatalogItem[]>>({});
  const [tagMap, setTagMap] = useState<Record<string, TagItem>>({});
  const [equippedSet, setEquippedSet] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch catalog + equipment + tags in parallel
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const client = getSkillClient();
        const categories = categoriesFor(currentAgent);

        // Fetch this agent's categories in parallel
        const catalogPromises = categories.map((cat) =>
          (async () => {
            const all: CatalogItem[] = [];
            let nextToken: string | undefined;
            do {
              const resp = await client.send(
                new ListCatalogCommand({ agentName: currentAgent, category: cat.apiCategory, maxResults: 50, nextToken }),
              );
              all.push(...(resp.items ?? []));
              nextToken = resp.nextToken ?? undefined;
            } while (nextToken);
            return all;
          })().catch(() => [] as CatalogItem[]),
        );

        // Fetch tags for each category
        const tagPromises = categories.map((cat) =>
          client.send(new ListTagsCommand({ agentName: currentAgent, category: cat.apiCategory }))
            .then((r) => r.items ?? [])
            .catch(() => [] as TagItem[]),
        );

        // Fetch equipped skills
        const equipPromise = activeUid
          ? client.send(new ListEquipmentCommand({ userAlias: activeUid }))
          : Promise.resolve({ items: [] });

        const [equipResp, catalogResults, tagResults] = await Promise.all([
          equipPromise,
          Promise.all(catalogPromises),
          Promise.all(tagPromises),
        ]);

        if (cancelled) return;

        const map: Record<string, CatalogItem[]> = {};
        categories.forEach((cat, i) => { map[cat.label] = catalogResults[i]; });
        setItemsByCategory(map);

        // Build tag lookup: tagName → TagItem
        const tMap: Record<string, TagItem> = {};
        tagResults.flat().forEach((t) => { if (t.tagName) tMap[t.tagName] = t; });
        setTagMap(tMap);

        const equipped = new Set(
          (equipResp.items ?? []).map((e) => `${e.agentName}::${e.skillName}`),
        );
        setEquippedSet(equipped);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeUid, currentAgent]);

  const handleToggle = useCallback(async (skillName: string, agentName: string, equip: boolean) => {
    if (!activeUid) return;
    const client = getSkillClient();
    const key = `${agentName}::${skillName}`;
    try {
      if (equip) {
        await client.send(new EquipSkillCommand({ userAlias: activeUid, agentName, skillName }));
        setEquippedSet((prev) => new Set(prev).add(key));
      } else {
        await client.send(new UnequipSkillCommand({ userAlias: activeUid, agentName, skillName }));
        setEquippedSet((prev) => { const next = new Set(prev); next.delete(key); return next; });
      }
    } catch (err) {
      console.error('Failed to toggle skill:', err);
    }
  }, [activeUid]);

  const isEquipped = useCallback(
    (skillName: string, agentName: string) => equippedSet.has(`${agentName}::${skillName}`),
    [equippedSet],
  );

  return { itemsByCategory, tagMap, isEquipped, handleToggle, loading, error };
}

// ── Component ───────────────────────────────────────────────────────────────

export default function SkillMarketplace() {
  const { t } = useTranslation('skills');
  const { currentAgent } = useAgent();
  const { locale } = useLocale();
  // Each agent gets a tab per category it has (CATEGORIES[].agents); an agent with
  // none sees just Recommend + My Skills.
  const agentCategories = useMemo(() => categoriesFor(currentAgent), [currentAgent]);
  const visibleTabs = useMemo(
    () => ['Recommend', ...agentCategories.map((c) => c.label), 'My Skills'],
    [agentCategories],
  );
  const [activeTab, setActiveTab] = useState<string>('Recommend');
  const [searchQuery, setSearchQuery] = useState('');

  // If the current tab is no longer visible after an agent switch (e.g. was on
  // a rocky-only category tab, then switched to marketing), fall back to Recommend.
  useEffect(() => {
    if (!(visibleTabs as readonly string[]).includes(activeTab)) {
      setActiveTab('Recommend');
    }
  }, [visibleTabs, activeTab]);
  const [detailSkill, setDetailSkill] = useState<SkillCardProps | null>(null);
  const { itemsByCategory, tagMap, isEquipped, handleToggle, loading, error } = useSkillMarketplace();
  const { isImpersonating } = useImpersonation();
  const activeToggle = isImpersonating ? undefined : handleToggle;

  // Build SkillCardProps from CatalogItem with equipped state
  const toSkillCard = useCallback((item: CatalogItem): SkillCardProps => {
    const s3Location = item.s3Location;
    const card: SkillCardProps = {
      title: item.displayName || item.skillName || t('untitled'),
      description: skillDescription(item, locale),
      searchText: skillSearchText(item),
      tags: item.tags ?? item.keywords ?? [],
      skillName: item.skillName || '',
      agentName: item.agentName || '',
      enabled: isEquipped(item.skillName || '', item.agentName || ''),
      thumbnailUrl: deriveThumbnailUrl(s3Location),
    };
    card.onShowDetails = () => setDetailSkill(card);
    return card;
  }, [isEquipped, locale, t]);

  // Build category sections from API data
  const categorySections = useMemo((): Record<string, SkillCategorySectionProps[]> => {
    const sections: Record<string, SkillCategorySectionProps[]> = {};

    for (const config of agentCategories) {
      const items = itemsByCategory[config.label] ?? [];
      if (items.length === 0 && !loading) {
        sections[config.label] = [];
        continue;
      }
      // Group by all tags (a skill appears in every tag group it belongs to)
      const grouped: Record<string, CatalogItem[]> = {};
      for (const item of items) {
        const tags = item.tags?.length ? item.tags : [GENERAL_TAG];
        for (const tag of tags) {
          (grouped[tag] ??= []).push(item);
        }
      }
      sections[config.label] = Object.entries(grouped)
        .sort(([a], [b]) => {
          const orderA = tagMap[a]?.displayOrder ?? 999;
          const orderB = tagMap[b]?.displayOrder ?? 999;
          return orderA - orderB;
        })
        .map(([tag, tagItems]) => ({
          title: tagMap[tag]?.displayName || (tag === GENERAL_TAG ? t('generalTag') : tag),
          description: tagMap[tag]?.description || t(`categories.${config.apiCategory}`),
          gradient: config.gradient,
          skills: tagItems.map(toSkillCard),
        }));
    }

    // Recommend: flatten all categories, deduplicate by skillName+agentName
    const allSkills = Object.values(sections).flat().flatMap((s) => s.skills);
    const seen = new Set<string>();
    const uniqueSkills = allSkills.filter((s) => {
      const key = `${s.agentName}::${s.skillName}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    sections['Recommend'] = [{ title: '', description: '', gradient: 'blue', skills: uniqueSkills }];
    return sections;
  }, [agentCategories, itemsByCategory, tagMap, loading, toSkillCard, t]);

  // My Skills: only show equipped skills, filtered by search
  const mySkillsData = useMemo((): MySkillsCategory[] => {
    const q = searchQuery.toLowerCase();
    return agentCategories
      .map((config) => ({
        category: {
          title: config.label,
          image:
            config.apiCategory === 'hedwig'
              ? '/images/SkillMarketplace-Hedwig.png'
              : config.apiCategory === 'cheetah'
                ? '/images/SkillMarketplace-Cheetah.png'
                : config.apiCategory === 'ivt'
                  ? '/images/SkillMarketplace-IVT.png'
                  : '/images/SkillMarketplace-DataRetriever.png',
          bgColor: config.gradient === 'blue' ? '#dae8f7' : '#feffcc',
        },
        skills: (itemsByCategory[config.label] ?? [])
          .filter((item) => isEquipped(item.skillName || '', item.agentName || ''))
          .map(toSkillCard)
          .filter((s) => !q || s.title.toLowerCase().includes(q) || s.description.toLowerCase().includes(q) || (s.searchText ?? '').toLowerCase().includes(q) || s.tags.some((t) => t.toLowerCase().includes(q))),
      }))
      .filter((cat) => cat.skills.length > 0);
  }, [agentCategories, itemsByCategory, isEquipped, toSkillCard, searchQuery]);

  const filteredCategories = useMemo(
    () => filterCategories(categorySections[activeTab] ?? [], searchQuery),
    [categorySections, activeTab, searchQuery],
  );

  return (
    <div className="skill-marketplace">
      <HeroBanner skillCount={Object.values(itemsByCategory).reduce((sum, items) => sum + items.length, 0)} />

      <div className="skill-marketplace__filters">
        <div className="skill-marketplace__tabs">
          {visibleTabs.map((tab) => (
            <button
              key={tab}
              className={`skill-marketplace__tab ${activeTab === tab ? 'skill-marketplace__tab--active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {TAB_LABEL_KEYS[tab] ? t(TAB_LABEL_KEYS[tab]) : tab}
            </button>
          ))}
        </div>
      </div>

      <div className="skill-marketplace__categories">
        <div className="skill-marketplace__search">
          <svg className="skill-marketplace__search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M11.5 6.5a5 5 0 1 0-2 4l3.35 3.36a.75.75 0 0 0 1.06-1.06L10.55 9.5a5 5 0 0 0 .95-3z" stroke="#8c8c94" strokeWidth="1.2" fill="none" />
          </svg>
          <input
            type="text"
            className="skill-marketplace__search-input"
            placeholder={t('search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {loading && <div className="skill-marketplace__loading">{t('loading')}</div>}
        {error && <div className="skill-marketplace__error">{t('loadFailed', { message: error })}</div>}

        {!loading && !error && (
          activeTab === 'My Skills' ? (
            <MySkillsGrid categories={mySkillsData} onToggle={activeToggle} />
          ) : activeTab === 'Recommend' ? (
            filteredCategories.flatMap((cat) => cat.skills).length > 0 ? (
              <div className="skill-marketplace__grid">
                {filteredCategories.flatMap((cat) => cat.skills).map((skill, i) => (
                  <SkillCard key={`${skill.skillName || skill.title}-${i}`} {...skill} onToggle={activeToggle} />
                ))}
              </div>
            ) : (
              <div className="skill-marketplace__empty">{t('empty')}</div>
            )
          ) : filteredCategories.length > 0 ? (
            filteredCategories.map((cat) => (
              <SkillCategorySection key={cat.title} {...cat} onToggle={activeToggle} />
            ))
          ) : (
            <div className="skill-marketplace__empty">{t('empty')}</div>
          )
        )}
      </div>

      {detailSkill && (
        <SkillDetailModal
          skill={detailSkill}
          onClose={() => setDetailSkill(null)}
          onToggle={isImpersonating ? undefined : handleToggle}
        />
      )}
    </div>
  );
}
